import { Box, Container, Typography, Button, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';

/**
 * NotFound Component
 * 404 error page with helpful navigation options
 */
const NotFound = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isProvider, profile } = useAuth();

  // Get default route based on user role
  const getDefaultRoute = () => {
    if (isAdmin) return '/admin';
    if (isProvider) return '/clinic-dashboard';
    if (profile?.tenant_role === 'sponsor') return '/sponsor-dashboard';
    return '/dashboard';
  };

  // Suggest similar routes based on current path
  const getSuggestedRoutes = () => {
    const suggestions = [];
    const path = location.pathname.toLowerCase();

    if (path.includes('admin')) {
      suggestions.push({ path: '/admin', label: 'Admin Dashboard' });
      suggestions.push({ path: '/admin/patients', label: 'Patient Management' });
      suggestions.push({ path: '/admin/physicians', label: 'Physician Management' });
    } else if (path.includes('clinic')) {
      suggestions.push({ path: '/clinic-dashboard', label: 'Clinic Dashboard' });
      suggestions.push({ path: '/clinic/patients', label: 'Clinic Patients' });
    } else if (path.includes('patient')) {
      suggestions.push({ path: '/dashboard', label: 'Patient Dashboard' });
      suggestions.push({ path: '/appointments', label: 'Appointments' });
    } else {
      // General suggestions
      suggestions.push({ path: getDefaultRoute(), label: 'Dashboard' });
      suggestions.push({ path: '/appointments', label: 'Appointments' });
      suggestions.push({ path: '/profile', label: 'Profile' });
    }

    return suggestions.slice(0, 3); // Limit to 3 suggestions
  };

  const suggestedRoutes = getSuggestedRoutes();
  const defaultRoute = getDefaultRoute();

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: { xs: '4rem', sm: '6rem', md: '8rem' },
            fontWeight: 700,
            mb: 2,
            opacity: 0.9,
          }}
        >
          404
        </Typography>

        <Typography
          variant="h4"
          component="h2"
          sx={{
            mb: 2,
            fontWeight: 600,
          }}
        >
          Page Not Found
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            opacity: 0.9,
            fontSize: '1.1rem',
          }}
        >
          The page you're looking for doesn't exist or has been moved.
          <br />
          <Box component="code" sx={{ fontSize: '0.9rem', opacity: 0.8, mt: 1, display: 'block' }}>
            {location.pathname}
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<HomeIcon />}
            onClick={() => navigate(defaultRoute)}
            sx={{
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            Go to Dashboard
          </Button>

          <Button
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Go Back
          </Button>
        </Box>

        {suggestedRoutes.length > 0 && (
          <Box
            sx={{
              mt: 4,
              pt: 4,
              borderTop: '1px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              You might be looking for:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {suggestedRoutes.map((suggestion) => (
                <Button
                  key={suggestion.path}
                  variant="text"
                  startIcon={<SearchIcon />}
                  onClick={() => navigate(suggestion.path)}
                  sx={{
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {suggestion.label}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default NotFound;

